import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Observable, filter, first, of, switchMap } from 'rxjs';
import { UserDetails } from '@bn/api-interfaces';
import { UserDetailsStateService } from '@bn/auth/services';
import { FeatureFlag } from '@bn/shared/entities/enums';

/**
 *
 * Usage examples:
 *
 * Routing item example:
 *
 * {
 *    path: AppRoutes.Script,
 *    component: Scripts,
 *    title: 'Scripts',
 *    canActivate: [FeatureFlagGuard],
 *    data: {
 *      featureFlag: FeatureFlags.HttpScriptingBeta,
 *    }
 *  }
 *
 */
@Injectable({ providedIn: 'root' })
export class NegatedFeatureFlagGuard implements CanActivate {
  private readonly userDetailsService = inject(UserDetailsStateService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.userDetailsService.getUserDetails().pipe(
      filter((userDetails: UserDetails | null) => userDetails !== null),
      switchMap((userDetails: UserDetails | null) => {
        const features = userDetails?.FeatureFlags || [];
        const featureFlag = route.data['featureFlag'] as FeatureFlag;

        if (features.includes(featureFlag)) {
          this.router.navigate(['/errors/unauthorized']);
          return of(false);
        }

        return of(true);
      }),
      first(),
    );
  }
}
